import { useCallback, useEffect, useRef, useState } from 'react';
import {
  defaultDims,
  filterListingId,
  filtersId,
  filtersIdName,
  firstFilterId,
  footerId,
  headerId,
  lastFilterId,
  listingId,
  productListingId,
  SEARCH_BOTTOM_DISTANCE,
  sortId,
  staticHeight,
  titleId,
} from './constants';
import {
  getCordinate,
  getStyle,
  isHeightInViewPort,
  isInViewport,
} from './helpers';
import { useIntersection } from './useIntersection';
import { ScrollProps } from './types';

const Scroll = (props: ScrollProps) => {
  const [dims] = useState(defaultDims);
  const [position] = useState({ state: 'static' });
  const first = useRef(false);
  const last = useRef(false);
  const footer = useRef(false);
  const title = useRef(false);
  const sort = useRef(false);
  const filter = useRef(false);
  footer.current = useIntersection(footerId, '1%');
  title.current = useIntersection(titleId, '2%');
  sort.current = useIntersection(sortId, '2%');
  filter.current = useIntersection(filtersIdName, '2%');
  const handleDirection = useCallback(
    ({ dim, type }) => {
      if (!dim.staticTop) {
        dim.staticTop = dim.lastViewportTop;
      }
      dims.filtersHeight =
        document.querySelector(filterListingId)?.clientHeight || 0;
      dims.wrapperHeight = document.querySelector(listingId)?.clientHeight || 0;
      const inViewPort = isHeightInViewPort(filtersId, dims.headerHeight); // is filter height less tha view port
      if (dims.filtersHeight >= dims.wrapperHeight) {
        getStyle('static-state', dim);
        return;
      }
      if (type && typeof type === 'string') {
        getStyle(type, dim);
        return;
      }
      if (footer.current) {
        dim.staticTop = 0;
        getStyle('footer-show', dim);
        return;
      }
      if (title.current) {
        dim.staticTop = 0;
        getStyle('static-state', dim);
        return;
      }
      if (dim.top < staticHeight) {
        dim.staticTop = 0;
        getStyle('static-state', dim);
        return;
      }
      if (dims.titleTop > 0) {
        getStyle('static-state', dim);
        return;
      }
      if (inViewPort) {
        getStyle('top-fixed', dim);
        return;
      }

      if (dim.direction === 'up') {
        if (first.current || !filter.current) {
          dim.staticTop = 0;

          getStyle('top-fixed', dim);
          return;
        }
        getStyle('hang-state', dim);
        return;
      }
      if (dim.direction === 'down') {
        if (last.current || !filter.current) {
          dim.staticTop = 0;
          position.state = 'bottom-fixed';
          getStyle('bottom-fixed', dim);
          return;
        }
        if (!sort.current) {
          getStyle('hang-state', dim);
        }

        return;
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      dims.filtersHeight,
      dims.headerHeight,
      dims.titleTop,
      dims.wrapperHeight,
      position.state,
    ]
  );

  const handleScroll = useCallback(
    (type) => {
      dims.lastViewportTop = dims?.viewportTop || 0;
      dims.lastTop = dims?.top || 0;
      dims.top = window.pageYOffset;
      dims.direction = dims.lastTop <= dims.top ? 'down' : 'up';
      const filterTop = getCordinate(filtersId).top;
      const listingTop = getCordinate(productListingId).top;
      dims.titleTop = getCordinate(listingId).topMargin;
      dims.viewportTop = filterTop - listingTop - (dims.top - dims.lastTop);
      last.current = isInViewport(lastFilterId);
      first.current = isInViewport(firstFilterId, dims.headerHeight);
      if (props.isSearch) {
        dims.viewportTop = dims.viewportTop - SEARCH_BOTTOM_DISTANCE;
      }
      handleDirection({ dim: dims, type });
    },
    [dims, handleDirection, props.isSearch]
  );
  useEffect(() => {
    dims.headerHeight =
      (document.querySelector(headerId) as HTMLElement)?.offsetHeight || 0;
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [dims.filtersHeight, dims.headerHeight, dims.wrapperHeight, handleScroll]);
  useEffect(() => {
    if (props.isPage && props.isSearch) {
      handleScroll('hang-state');
    }
    if (props.isFilter) {
      handleScroll('static-state');
    }
  }, [handleScroll, props.isFilter, props.isPage, props.isSearch]);
  return null;
};

export default Scroll;
